import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/AdminLayout";
import { api, errText } from "@/lib/api";
import { PROSPECT_STATUSES, PROSPECT_STYLES, money0, timeAgo, dollars0 } from "@/lib/partners";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Plus, Upload, Download, Flame, BarChart3, Archive, Copy } from "lucide-react";

const SORTS = [
  { v: "created_at", l: "Newest" },
  { v: "prospect_score", l: "ARK Score" },
  { v: "estimated_transactions", l: "Transactions" },
  { v: "presentation_views", l: "Presentation Views" },
  { v: "last_viewed_at", l: "Recently Viewed" },
  { v: "revenue", l: "Revenue" },
  { v: "leads", l: "Leads" },
  { v: "display_name", l: "Name A–Z" },
];

export default function RealtorList() {
  const [rows, setRows] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [filters, setFilters] = useState({ search: "", prospect_status: "", city: "", brokerage: "", min_transactions: "", sort: "created_at", hot_only: false, archived: false });
  const [selected, setSelected] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async (f) => {
    try {
      const { data } = await api.get("/realtors", {
        params: {
          search: f.search, prospect_status: f.prospect_status, city: f.city, brokerage: f.brokerage,
          min_transactions: Number(f.min_transactions) || 0, sort: f.sort, hot_only: f.hot_only, archived: f.archived,
        },
      });
      setRows(data);
    } catch (e) {
      toast.error(errText(e));
      setRows([]);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(filters), 250);
    return () => clearTimeout(t);
  }, [filters, load]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const allSelected = rows?.length > 0 && selected.length === rows.length;

  const bulk = async (action, value) => {
    if (!selected.length) return toast.error("Select at least one realtor");
    try {
      const { data } = await api.post("/realtors/bulk", { ids: selected, action, value });
      toast.success(`${data.updated} realtor${data.updated === 1 ? "" : "s"} updated`);
      setSelected([]);
      load(filters);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/realtors/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "ark-realtor-partners.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/realtors/import", fd);
      toast.success(`${data.created} realtors imported${data.skipped ? `, ${data.skipped} duplicates skipped` : ""}`);
      if (data.errors?.length) toast.error(data.errors.slice(0, 3).join(" · "));
      load(filters);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <AdminLayout title="Realtor Partners">
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between mb-6">
        <div className="relative w-full lg:w-96">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="realtor-search-input"
            placeholder="Search name, brokerage, city, code..."
            value={filters.search}
            onChange={(e) => set("search", e.target.value)}
            className="pl-9 h-11 rounded-sm bg-white"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".csv" hidden onChange={(e) => importCsv(e.target.files?.[0])} />
          <Button data-testid="funnel-link-btn" asChild variant="outline" className="rounded-sm h-11 text-xs uppercase tracking-wider">
            <Link to="/admin/partner-funnel"><BarChart3 size={14} className="mr-1.5" /> Funnel</Link>
          </Button>
          <Button data-testid="import-realtors-btn" onClick={() => fileRef.current.click()} disabled={importing} variant="outline" className="rounded-sm h-11 text-xs uppercase tracking-wider">
            <Upload size={14} className="mr-1.5" /> {importing ? "Importing..." : "Import CSV"}
          </Button>
          <Button data-testid="export-realtors-btn" onClick={exportCsv} variant="outline" className="rounded-sm h-11 text-xs uppercase tracking-wider">
            <Download size={14} className="mr-1.5" /> Export
          </Button>
          <Button data-testid="new-realtor-btn" asChild className="bg-black hover:bg-black/85 text-white rounded-sm h-11 px-6 tracking-[0.1em] uppercase text-xs btn-lift">
            <Link to="/admin/realtors/new"><Plus size={16} className="mr-1" /> New Realtor</Link>
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <Select value={filters.prospect_status || "all"} onValueChange={(v) => set("prospect_status", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-status" className="w-48 h-10 rounded-sm bg-white"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {PROSPECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="filter-city" placeholder="City" value={filters.city} onChange={(e) => set("city", e.target.value)} className="w-36 h-10 rounded-sm bg-white" />
        <Input data-testid="filter-brokerage" placeholder="Brokerage" value={filters.brokerage} onChange={(e) => set("brokerage", e.target.value)} className="w-40 h-10 rounded-sm bg-white" />
        <Input data-testid="filter-transactions" placeholder="Min transactions" value={filters.min_transactions} onChange={(e) => set("min_transactions", e.target.value)} className="w-40 h-10 rounded-sm bg-white" />
        <Select value={filters.sort} onValueChange={(v) => set("sort", v)}>
          <SelectTrigger data-testid="filter-sort" className="w-48 h-10 rounded-sm bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>{SORTS.map((s) => <SelectItem key={s.v} value={s.v}>Sort: {s.l}</SelectItem>)}</SelectContent>
        </Select>
        <Button
          data-testid="filter-hot"
          onClick={() => set("hot_only", !filters.hot_only)}
          variant={filters.hot_only ? "default" : "outline"}
          className={`h-10 rounded-sm text-xs uppercase tracking-wider ${filters.hot_only ? "bg-[#c19a6b] hover:bg-[#b08a5c] text-white" : ""}`}
        >
          <Flame size={14} className="mr-1.5" /> Hot
        </Button>
        <Button
          data-testid="filter-archived"
          onClick={() => set("archived", !filters.archived)}
          variant={filters.archived ? "default" : "outline"}
          className="h-10 rounded-sm text-xs uppercase tracking-wider"
        >
          <Archive size={14} className="mr-1.5" /> {filters.archived ? "Archived" : "Active"}
        </Button>
      </div>

      {selected.length > 0 && (
        <div className="bg-black text-white px-4 py-3 mb-4 flex flex-wrap items-center gap-3" data-testid="bulk-bar">
          <span className="text-sm">{selected.length} selected</span>
          <Button data-testid="bulk-ready-btn" onClick={() => bulk("mark_ready")} size="sm" className="h-8 bg-[#c19a6b] hover:bg-[#b08a5c] rounded-sm text-[11px] uppercase tracking-wider">Mark Ready to Contact</Button>
          <Button data-testid="bulk-contacted-btn" onClick={() => bulk("mark_contacted")} size="sm" variant="outline" className="h-8 rounded-sm text-[11px] uppercase tracking-wider text-black">Mark Contacted</Button>
          <Select onValueChange={(v) => bulk("set_prospect_status", v)}>
            <SelectTrigger data-testid="bulk-status-select" className="w-44 h-8 rounded-sm text-black bg-white text-xs"><SelectValue placeholder="Change status" /></SelectTrigger>
            <SelectContent>{PROSPECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="bulk-archive-btn" onClick={() => bulk(filters.archived ? "unarchive" : "archive")} size="sm" variant="outline" className="h-8 rounded-sm text-[11px] uppercase tracking-wider text-black">
            {filters.archived ? "Restore" : "Archive"}
          </Button>
          {campaigns.length > 0 && (
            <Select onValueChange={(v) => bulk("assign_campaign", v)}>
              <SelectTrigger data-testid="bulk-campaign-select" className="w-44 h-8 rounded-sm text-black bg-white text-xs"><SelectValue placeholder="Assign campaign" /></SelectTrigger>
              <SelectContent>{campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <button onClick={() => setSelected([])} className="ml-auto text-xs text-white/60 hover:text-white underline underline-offset-4">Clear</button>
        </div>
      )}

      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="realtors-table">
          <thead>
            <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-[0.12em] text-gray-500">
              <th className="px-3 py-3">
                <Checkbox
                  data-testid="select-all-realtors"
                  checked={allSelected}
                  onCheckedChange={(v) => setSelected(v ? rows.map((r) => r.id) : [])}
                />
              </th>
              <th className="px-4 py-3 font-medium">Realtor</th>
              <th className="px-4 py-3 font-medium">Brokerage</th>
              <th className="px-4 py-3 font-medium">City</th>
              <th className="px-4 py-3 font-medium text-right">Txns</th>
              <th className="px-4 py-3 font-medium text-right">Score</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Pres. Views</th>
              <th className="px-4 py-3 font-medium">Last Viewed</th>
              <th className="px-4 py-3 font-medium text-right">Client Visits</th>
              <th className="px-4 py-3 font-medium text-right">Leads</th>
              <th className="px-4 py-3 font-medium text-right">Sales</th>
              <th className="px-4 py-3 font-medium text-right">Revenue</th>
              <th className="px-4 py-3 font-medium">Links</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={14} className="px-4 py-12 text-center text-gray-400">Loading...</td></tr>}
            {rows?.length === 0 && (
              <tr><td colSpan={14} className="px-4 py-16 text-center text-gray-400" data-testid="no-realtors-message">
                No realtors yet. Create your first partner presentation.
              </td></tr>
            )}
            {rows?.map((r) => (
              <tr key={r.id} data-testid={`realtor-row-${r.slug}`} className="border-b border-gray-100 hover:bg-[#FAF7F2] transition-colors duration-150">
                <td className="px-3 py-3.5" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    data-testid={`select-realtor-${r.slug}`}
                    checked={selected.includes(r.id)}
                    onCheckedChange={(v) => setSelected((s) => (v ? [...s, r.id] : s.filter((x) => x !== r.id)))}
                  />
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap cursor-pointer" onClick={() => navigate(`/admin/realtors/${r.id}`)}>
                  <span className="font-semibold">{r.display_name}</span>
                  {r.hot && (
                    <span className="ml-2 inline-flex items-center gap-1 bg-[#c19a6b] text-white text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5" data-testid={`hot-flag-${r.slug}`}>
                      <Flame size={10} /> Hot
                    </span>
                  )}
                  {r.partner_status === "Activated" && <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-emerald-600">Activated</span>}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap">{r.brokerage || "—"}</td>
                <td className="px-4 py-3.5 whitespace-nowrap">{r.city || "—"}</td>
                <td className="px-4 py-3.5 text-right">{r.estimated_transactions || "—"}</td>
                <td className="px-4 py-3.5 text-right">{r.prospect_score ? `${r.prospect_score}` : "—"}</td>
                <td className="px-4 py-3.5 whitespace-nowrap">
                  <span className={`text-[11px] px-2 py-1 font-semibold tracking-wide ${PROSPECT_STYLES[r.prospect_status] || "bg-gray-100"}`}>{r.prospect_status}</span>
                </td>
                <td className="px-4 py-3.5 text-right">{r.presentation_views}</td>
                <td className="px-4 py-3.5 text-gray-500 whitespace-nowrap">{r.last_viewed_at ? timeAgo(r.last_viewed_at) : "—"}</td>
                <td className="px-4 py-3.5 text-right">{r.client_page_views}</td>
                <td className="px-4 py-3.5 text-right">{r.metrics.leads}</td>
                <td className="px-4 py-3.5 text-right">{r.metrics.sales}</td>
                <td className="px-4 py-3.5 text-right font-semibold whitespace-nowrap">{money0(r.metrics.revenue_cents)}</td>
                <td className="px-4 py-3.5 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <a href={r.links.presentation_url} target="_blank" rel="noreferrer" data-testid={`open-presentation-${r.slug}`} className="text-[11px] uppercase tracking-wider text-[#8a6a42] hover:text-black underline underline-offset-2">Pitch</a>
                    <a href={r.links.client_url} target="_blank" rel="noreferrer" data-testid={`open-client-${r.slug}`} className="text-[11px] uppercase tracking-wider text-[#8a6a42] hover:text-black underline underline-offset-2">Client</a>
                    <button
                      data-testid={`copy-client-${r.slug}`}
                      onClick={() => { navigator.clipboard.writeText(r.links.client_url); toast.success("Client link copied"); }}
                      className="text-gray-400 hover:text-black transition-colors duration-150"
                    >
                      <Copy size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows?.length > 0 && (
        <p className="text-xs text-gray-400 mt-4">
          {rows.length} realtor{rows.length === 1 ? "" : "s"} · Combined estimated volume{" "}
          {dollars0(rows.reduce((a, r) => a + (r.estimated_volume || 0), 0))}
        </p>
      )}
    </AdminLayout>
  );
}
